import PropTypes from 'prop-types'
import R from 'ramda'
import { createContext, useContext, useEffect, useReducer } from 'react'

import getInitialState from 'Common/utils/getInitialState'
import useUserAccess from 'Common/hooks/useUserAccess'

import actions from './actions'
import foldersReducer from './reducer'
import services from '../services'

// Context objects
const FoldersState = createContext()
const FoldersDispatch = createContext()

FoldersState.displayName = 'FoldersState'
FoldersDispatch.displayName = 'FoldersDispatch'

// Context hook
function useFoldersContext() {
  const state = useContext(FoldersState)
  const dispatch = useContext(FoldersDispatch)

  if (state === undefined || dispatch === undefined) {
    throw new Error('useFoldersContext must be used within a FoldersProvider')
  }

  return [state, dispatch]
}

function FoldersProvider({ children }) {
  const {
    myFolders,
    publishedFolders,
    sharedFolders,
    school,
    users,
  } = getInitialState()

  const initialState = {
    myFolders,
    publishedFolders,
    sharedFolders,
    school,
    users,
    currentFolderId: null, // Will be updated by WatchCurrentFolder in Folders app; otherwise not used
    hasNewPublished: false,
    hasNewShared: false,
    isPublisher: R.any(R.has('eslPublishedAt'), myFolders),
    sort: 'manual',
    sharedSort: 'ascending',
  }

  const [foldersState, setFoldersState] = useReducer(
    foldersReducer,
    initialState
  )

  return (
    <FoldersState.Provider value={foldersState}>
      <FoldersDispatch.Provider value={setFoldersState}>
        {children}
      </FoldersDispatch.Provider>
    </FoldersState.Provider>
  )
}

FoldersProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

// Context provider for the MoveItemModal used on content pages with API-based initial state
// `fallback` node will be rendered until folders have been fetched
function ModalFoldersProvider({ fallback, children }) {
  // [1]
  // We need to store the initial state as a variable so we can compare referential equality (see [2]).
  // (This avoids the use of a useState just for tracking whether the fetch has completed or not.)
  const initialFolders = []
  const { userLoggedIn } = useUserAccess()

  const [foldersState, setFoldersState] = useReducer(foldersReducer, {
    myFolders: initialFolders,
    userLoggedIn,
  })

  // Fetch the user's folders when the Provider first mounts
  useEffect(() => {
    async function fetchFolders() {
      const { data: fetchedFolders } = await services.getFolders()
      return setFoldersState(actions.setFolders(fetchedFolders))
    }

    userLoggedIn && fetchFolders()
  }, [userLoggedIn])

  // [2]
  // If the folders haven't been fetched yet, display the fallback.
  // If the state is referentially equal to our initial empty state, the fetch hasn't been completed yet.
  if (foldersState.myFolders === initialFolders) {
    return fallback
  }

  return (
    <FoldersState.Provider value={foldersState}>
      <FoldersDispatch.Provider value={setFoldersState}>
        {children}
      </FoldersDispatch.Provider>
    </FoldersState.Provider>
  )
}

ModalFoldersProvider.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.node.isRequired,
}

export { FoldersProvider, ModalFoldersProvider, useFoldersContext }
